import { useContext, useEffect, useState } from 'react'
import { Mic } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import { ExampleList } from '../components/ExampleList';
import { PracticeModal } from '../components/PracticeModal';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { getSuggestions, searchWord } from '../services/dictionary';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../store/useStore';
// import { captureException } from '@sentry/react';

const Practice = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [examples, setExamples] = useState<Array<{text: string, partOfSpeech: string}>>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedExample, setSelectedExample] = useState(null);
  const { isRecording, isStarting, transcript, startRecording, stopRecording } = useSpeechRecognition({
    onResult: () => {},
    onEnd: () => {},
    onInterimResult : () => {},
    isFromPractice: true
  });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const { updateWordProgress } = useContext(UserContext);
  const { state } = useLocation();
  const [word, setWord] = useState('');

  const initialPracticeState = {
    isRecording: false,
    transcription: '',
    audioBlob: null,
    attempts: 0,
    score: null,
  };

  const [practiceState, setPracticeState] = useState(initialPracticeState);

  const handleStartRecording = () => {
    startRecording();
      updateWordProgress(word, state?.themeName)
    setPracticeState(prevState => ({
      ...prevState,
      attempts: prevState.attempts + 1,
    }));
  };

  const handleSearch = async (word: string) => {
    setIsLoading(true);
    setError(null);
    // Allow multiple words so users can practice speaking expressions/phrases
    try {
      // Use the unified searchWord function that handles both APIs
      const wordData = await searchWord(word);
      
      if (!wordData || !wordData.examples || wordData.examples.length === 0) {
        throw new Error('No examples found for this word');
      }

      // Transform examples to the format expected by ExampleList component
      const newExamples = wordData.examples.map((example) => ({
        text: example,
        partOfSpeech: wordData.meanings[0]?.partOfSpeech || 'unknown'
      }));

      console.log("newExamples", newExamples);
      console.log("wordData", wordData);
      
      setExamples(newExamples);
    } catch (error) {
      console.error('Error fetching word:', error);
      setError(`Word ${word} not found. Please try another word.`);
      setExamples([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = async (word: string) => {
    if (word.length > 1) {
      const newSuggestions = await getSuggestions(word);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleClosePractice = () => {
    setSelectedExample(null);
    setPracticeState(initialPracticeState);
    stopRecording();
  };


  const handleSuggestionSelect = (word: string) => {
    handleSearch(word);
  };

  useEffect(() => {
    if (state && state.word) {
      handleSearch(state.word);
      setWord(state.word);
    }
  }, [state]);

  useEffect(() => {
    setPracticeState(prevState => ({
      ...prevState,
      isRecording: isRecording,
      transcription: transcript,
    }));
  }, [isRecording, transcript]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <div className="container mx-auto py-12">
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Mic className="w-12 h-12 text-purple-600" />
            <h1 className="text-4xl font-bold text-gray-900">Speak Perfecly</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Improve your English pronunciation with real-time feedback and practice
          </p>
        </header>

        <main className="flex flex-col items-center gap-8 p-6 w-full lg:w-[50%] mx-auto">
          <SearchBar 
            isLoading={isLoading} 
            onSearch={handleSearch} 
            suggestions={suggestions}
            onSuggestionSelect={handleSuggestionSelect}
            onInputChange={handleInputChange}
            wordParam={state?.word}
            setWordContext={setWord}
          />
           {error && (
              <div className={`p-4 rounded-lg mb-6 bg-red-100 text-red-700`}>
                {error}
              </div>
            )}
          <ExampleList
            examples={examples}
            onPractice={(example) => setSelectedExample(example)}
          />
        </main>

        {selectedExample && (
          <PracticeModal
            example={selectedExample}
            practiceState={practiceState}
            onClose={handleClosePractice}
            onStartRecording={handleStartRecording}
            onStopRecording={stopRecording}
            isRecording={isRecording}
            isStarting={isStarting}
          />
        )}
      </div>
    </div>
  );
};

export default Practice;
